// ============================================
// Dispatcher Login Page — Operations-Branded
// URL: /dispatcher/login
// ============================================

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { loginUser, clearError } from '../../redux/slices/authSlice';
import { useTheme } from '../../context/ThemeContext';

const DispatcherLogin = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { isAuthenticated, isLoading, error, user } = useSelector((state) => state.auth);
    const { theme, toggleTheme } = useTheme();

    const [formData, setFormData] = useState({ email: '', password: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [localError, setLocalError] = useState('');

    useEffect(() => {
        if (isAuthenticated && user) {
            if (user.role === 'dispatcher') {
                navigate('/dispatcher', { replace: true });
            } else {
                const routes = { admin: '/admin/login', driver: '/login', user: '/login' };
                navigate(routes[user.role] || '/login', { replace: true });
            }
        }
    }, [isAuthenticated, user, navigate]);

    useEffect(() => () => dispatch(clearError()), [dispatch]);

    const handleChange = (e) => {
        setLocalError('');
        if (error) dispatch(clearError());
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setLocalError('');
        dispatch(clearError());

        if (!formData.email.trim()) {
            return setLocalError('Please provide your dispatcher email.');
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            return setLocalError("Please enter a valid email address.");
        }
        if (!formData.password) {
            return setLocalError('Please enter your password.');
        }

        dispatch(loginUser({ ...formData, loginRole: 'dispatcher' }));
    };

    return (
        <div className="h-screen w-screen flex bg-slate-50 dark:bg-[#050510] font-sans transition-colors duration-500 overflow-hidden aurora-bg relative">
            {/* ── Left Panel: Dispatcher Branding ────────── */}
            <div className="hidden lg:flex lg:w-[480px] xl:w-[560px] relative overflow-hidden bg-gradient-to-br from-emerald-950 via-slate-900 to-emerald-950 dark:from-slate-950 dark:via-emerald-950 dark:to-slate-900 transition-colors duration-500 border-r border-white/10 dark:border-white/5">
                {/* Animated Background */}
                <div className="absolute inset-0">
                    <div className="absolute top-20 -left-20 w-72 h-72 bg-emerald-500/8 rounded-full blur-3xl animate-pulse" />
                    <div className="absolute bottom-32 -right-16 w-96 h-96 bg-teal-500/6 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
                    <div className="absolute top-1/2 left-1/3 w-48 h-48 bg-cyan-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '4s' }} />

                    {/* Grid Pattern */}
                    <div className="absolute inset-0 opacity-[0.03]"
                        style={{
                            backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
                            backgroundSize: '60px 60px'
                        }}
                    />

                    {/* Radar/Dispatch SVG */}
                    <svg className="absolute inset-0 w-full h-full opacity-[0.04]" viewBox="0 0 560 900">
                        <circle cx="280" cy="450" r="120" stroke="url(#dispGrad)" strokeWidth="1.5" fill="none" />
                        <circle cx="280" cy="450" r="200" stroke="url(#dispGrad)" strokeWidth="1" fill="none" strokeDasharray="8 4" />
                        <circle cx="280" cy="450" r="280" stroke="url(#dispGrad)" strokeWidth="0.8" fill="none" strokeDasharray="4 6" />
                        <line x1="280" y1="330" x2="340" y2="400" stroke="url(#dispGrad)" strokeWidth="2" />
                        <circle cx="280" cy="450" r="5" fill="#10b981" opacity="0.5" />
                        <circle cx="340" cy="400" r="3" fill="#10b981" opacity="0.4" />
                        <circle cx="220" cy="480" r="3" fill="#10b981" opacity="0.4" />
                        <circle cx="310" cy="520" r="3" fill="#10b981" opacity="0.4" />
                        <defs>
                            <linearGradient id="dispGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#10b981" />
                                <stop offset="100%" stopColor="#06b6d4" />
                            </linearGradient>
                        </defs>
                    </svg>
                </div>

                {/* Content */}
                <div className="relative z-10 flex flex-col justify-between p-12 w-full">
                    {/* Logo */}
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/25">
                            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                            </svg>
                        </div>
                        <div>
                            <span className="text-white/90 font-bold text-xl tracking-tight">IdaCab</span>
                            <span className="block text-emerald-400/80 text-xs font-semibold tracking-widest uppercase">Dispatch Center</span>
                        </div>
                    </div>

                    {/* Hero */}
                    <div className="my-auto">
                        <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-6">
                            Live Fleet
                            <span className="block mt-2 bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent">
                                Operations
                            </span>
                            Center
                        </h1>
                        <p className="text-slate-400 text-lg leading-relaxed max-w-md">
                            Real-time driver tracking, job assignment, route monitoring, and instant alert management.
                        </p>

                        <div className="flex flex-wrap gap-3 mt-8">
                            {['Live Tracking', 'Job Assignment', 'Route Monitoring', 'Alert System'].map((f, i) => (
                                <span key={i} className="px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-300/80 text-xs font-medium">
                                    {f}
                                </span>
                            ))}
                        </div>
                    </div>

                    <div className="text-slate-600 text-xs">
                        © 2026 IdaCab — Operations Team Access
                    </div>
                </div>
            </div>

            {/* ── Right Panel: Next-Gen Login Form ──────────────── */}
            <div className="flex-1 h-full relative selection:bg-emerald-500/20 z-10 flex flex-col justify-center items-center p-4 sm:p-8 lg:p-20 overflow-hidden">
                
                {/* Global Theme Toggle */}
                <div className="absolute top-6 right-6 z-50">
                    <button
                        onClick={toggleTheme}
                        className="p-3 rounded-full bg-white/80 dark:bg-slate-800/50 backdrop-blur-xl text-slate-500 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-all border border-slate-200 dark:border-slate-700 hover:scale-105 active:scale-95 shadow-lg shadow-black/5"
                        aria-label="Toggle theme"
                    >
                        {theme === 'light' ? (
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                            </svg>
                        ) : (
                            <svg className="w-5 h-5 text-amber-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                        )}
                    </button>
                </div>

                <div className="w-full max-w-[460px] bg-white dark:bg-slate-900/60 backdrop-blur-3xl rounded-[2.5rem] p-6 sm:p-10 shadow-2xl shadow-slate-200 dark:shadow-black/50 border border-slate-100 dark:border-white/5 transition-colors duration-500 relative mt-4 mb-4 sm:my-0">
                    {/* Mobile Logo */}
                    <div className="lg:hidden flex items-center gap-3 mb-10 justify-center">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                            </svg>
                        </div>
                        <div>
                            <span className="font-bold text-xl text-slate-800 dark:text-white">IdaCab</span>
                            <span className="block text-emerald-600 dark:text-emerald-400 text-xs font-semibold">Dispatch Center</span>
                        </div>
                    </div>

                    {/* Dispatcher Badge */}
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-100 dark:border-emerald-500/20 mb-6 transition-colors">
                        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                        <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-400">Dispatch Portal</span>
                    </div>

                    <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-1 transition-colors">Dispatcher Sign In</h2>
                    <p className="text-slate-500 dark:text-slate-400 text-sm mb-8 transition-colors">Access live fleet operations</p>

                    {/* Error */}
                    {(error || localError) && (
                        <div className="mb-6 flex items-start gap-3 p-3.5 rounded-xl bg-red-50/80 dark:bg-red-500/10 border border-red-200/80 dark:border-red-500/20 backdrop-blur-sm transition-all duration-300 animate-in fade-in slide-in-from-top-2">
                            <div className="mt-0.5 text-red-600 dark:text-red-400 flex-shrink-0">
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <p className="text-red-700 dark:text-red-300 text-[13px] font-semibold leading-snug tracking-wide">
                                {localError || (error === 'Login failed' ? 'Invalid email or password' : error)}
                            </p>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5">
                        {/* Email */}
                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 transition-colors">Dispatcher Email</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                    <svg className="w-5 h-5 text-slate-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                </div>
                                <input
                                    name="email" type="text" value={formData.email} onChange={handleChange}
                                    className="w-full pl-12 pr-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-800/50 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all text-sm backdrop-blur-sm"
                                    placeholder="dispatcher@idacab.com"
                                />
                            </div>
                        </div>

                        {/* Password */}
                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 transition-colors">Password</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                    <svg className="w-5 h-5 text-slate-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                </div>
                                <input
                                    name="password" type={showPassword ? 'text' : 'password'} value={formData.password} onChange={handleChange}
                                    className="w-full pl-12 pr-12 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-800/50 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all text-sm backdrop-blur-sm"
                                    placeholder="Enter dispatcher password"
                                />
                                <button type="button" onClick={() => setShowPassword(!showPassword)}
                                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors">
                                    {showPassword ? (
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" /></svg>
                                    ) : (
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                    )}

                                </button>
                            </div>
                            <div className="flex justify-end mt-2">
                                <Link to="/secure-recovery?role=dispatcher" className="text-xs font-bold text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300 transition-colors">
                                    Forgot Password?
                                </Link>
                            </div>
                        </div>

                        {/* Submit */}
                        <button type="submit" disabled={isLoading}
                            className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold text-sm shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                            {isLoading ? (
                                <>
                                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                                    Connecting...
                                </>
                            ) : (
                                <>
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                                    </svg>
                                    Sign In as Dispatcher
                                </>
                            )}
                        </button>
                    </form>

                    <p className="text-center text-xs text-slate-400 dark:text-slate-500 mt-8 transition-colors">
                        This portal is for authorized dispatch operators only.<br />
                        Contact admin for access credentials.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default DispatcherLogin;
